import { useCallback, useEffect, useState } from "react";
import { Grid, Button, Skeleton } from "@mui/material";
import { useIntl } from "react-intl";
import { cloneDeep, isEqual } from "lodash-es";

import AddIcon from "@mui/icons-material/Add";

import { useSmartalarm } from "../SmartalarmTemplate";
import { Partition, SmartalarmUser } from "../types";
import RfidReader from "./RfidReader";
import { genSaltString, uuidv4 } from "../../../../../utils/utils";
import TitledCard from "../../TitledCard";

const RfidReaders = () => {
  const [readerIds, setReaderIds] = useState<string[] | null>(null);
  const [partitions, setPartitions] = useState<Partition[]>([]);
  const [boards, setBoards] = useState<{ uuid: string; name: string; deleted?: boolean }[]>([]);
  const [users, setUsers] = useState<SmartalarmUser[]>([]);

  const { value, saved, setValue } = useSmartalarm();

  const { formatMessage } = useIntl();

  const onReaderChange = useCallback(
    (index, v) => {
      value.rfids[index] = v;
      setValue(value);
    },
    [value, setValue]
  );

  const addNewRfidReader = useCallback(() => {
    value.rfids.push({ uuid: uuidv4(), salt: genSaltString(), name: "", board: "", protocol: null, partition: "ALL", exitDelay: 0, cards: [] });
    setReaderIds(value.rfids.map((reader) => reader.uuid));
    setValue(value);
  }, [value, setValue]);

  useEffect(() => {
    if (!value) {
      return;
    }
    setPartitions((old) => (isEqual(old, value.plists.partitions) ? old : value.plists.partitions || []));
    setBoards((old) => {
      const boards = value.panels.map((p, i) => ({
        uuid: p.uuid,
        name: p.name || (i === 0 ? formatMessage({ defaultMessage: "Master panel" }) : formatMessage({ defaultMessage: "Slave panel #{index}" }, { index: i })),
        deleted: p.deleted,
      }));
      return isEqual(old, boards) ? old : boards;
    });
    setUsers((old) => (isEqual(old, value.users) ? old : cloneDeep(value.users) || []));
    setReaderIds(value.rfids.map((reader) => reader.uuid));
  }, [value, formatMessage]);

  if (!readerIds) {
    return (
      <TitledCard>
        <Grid container spacing={1} sx={{ padding: 1 }}>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
        </Grid>
      </TitledCard>
    );
  }
  return (
    <Grid container spacing={1}>
      {readerIds.length === value?.rfids.length &&
        readerIds.map((id, index) => (
          <Grid item xs={12} key={`reader-${id}`}>
            <RfidReader
              index={index}
              value={value.rfids[index]}
              saved={saved?.rfids[index] || null}
              partitions={partitions}
              boards={boards}
              users={users}
              onChange={onReaderChange}
            />
          </Grid>
        ))}
      <Grid item xs={12}>
        <Button startIcon={<AddIcon />} onClick={addNewRfidReader}>
          {formatMessage({ defaultMessage: "New RFID reader configuration" })}
        </Button>
      </Grid>
    </Grid>
  );
};

export default RfidReaders;
